import { Component } from '@theme/component';

class SimucubeCustomerStudies extends Component {
  requiredRefs = ['grid'];

  #frame = null;
  #pointerId = null;
  #startX = 0;
  #startScroll = 0;
  #dragged = false;

  connectedCallback() {
    super.connectedCallback();
    const { grid } = this.refs;
    grid.addEventListener('scroll', this.#queueUpdate, { passive: true });
    window.addEventListener('resize', this.#queueUpdate);
    grid.addEventListener('pointerdown', this.#onPointerDown);
    grid.addEventListener('click', this.#onClickCapture, true);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { grid } = this.refs;
    grid.removeEventListener('scroll', this.#queueUpdate);
    window.removeEventListener('resize', this.#queueUpdate);
    grid.removeEventListener('pointerdown', this.#onPointerDown);
    grid.removeEventListener('click', this.#onClickCapture, true);
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  #queueUpdate = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#update();
    });
  };

  #update() {
    const { grid, track, bar } = this.refs;
    if (!track || !bar) return;

    const max = grid.scrollWidth - grid.clientWidth;
    if (max <= 0) {
      bar.style.width = '100%';
      bar.style.transform = 'translateX(0)';
      return;
    }

    const ratio = grid.clientWidth / grid.scrollWidth;
    const thumbWidth = Math.max(track.clientWidth * ratio, 24);
    const progress = grid.scrollLeft / max;
    const offset = progress * (track.clientWidth - thumbWidth);

    bar.style.width = `${Math.round(thumbWidth)}px`;
    bar.style.transform = `translateX(${Math.round(offset)}px)`;
  }

  // Touch scrolling is native; this adds click-and-drag panning for mouse users.
  #onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const { grid } = this.refs;
    if (grid.scrollWidth <= grid.clientWidth) return;

    this.#pointerId = event.pointerId;
    this.#startX = event.clientX;
    this.#startScroll = grid.scrollLeft;
    this.#dragged = false;
    grid.addEventListener('pointermove', this.#onPointerMove);
    grid.addEventListener('pointerup', this.#onPointerUp);
    grid.addEventListener('pointercancel', this.#onPointerUp);
  };

  #onPointerMove = (event) => {
    if (event.pointerId !== this.#pointerId) return;
    const delta = event.clientX - this.#startX;
    if (!this.#dragged && Math.abs(delta) < 5) return;

    const { grid } = this.refs;
    if (!this.#dragged) {
      this.#dragged = true;
      this.setAttribute('data-dragging', '');
      try {
        grid.setPointerCapture(this.#pointerId);
      } catch {
        // pointer may already be gone (e.g. released mid-frame); dragging still works uncaptured
      }
    }
    grid.scrollLeft = this.#startScroll - delta;
  };

  #onPointerUp = (event) => {
    if (event.pointerId !== this.#pointerId) return;
    const { grid } = this.refs;
    grid.removeEventListener('pointermove', this.#onPointerMove);
    grid.removeEventListener('pointerup', this.#onPointerUp);
    grid.removeEventListener('pointercancel', this.#onPointerUp);
    if (grid.hasPointerCapture(this.#pointerId)) {
      grid.releasePointerCapture(this.#pointerId);
    }
    this.#pointerId = null;
    // Keep data-dragging (and #dragged) through the click that follows pointerup
    // so the capture-phase handler can swallow it, then reset.
    requestAnimationFrame(() => {
      this.removeAttribute('data-dragging');
      this.#dragged = false;
    });
  };

  #onClickCapture = (event) => {
    if (!this.#dragged) return;
    event.preventDefault();
    event.stopPropagation();
  };
}

if (!customElements.get('simucube-customer-studies')) {
  customElements.define('simucube-customer-studies', SimucubeCustomerStudies);
}
